import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";

import { useSidebar } from "../../../contexts/SidebarContext";
import {
  useChooseAnswerMutation,
  useFinishExamMutation,
  useGetUserExamByIdQuery,
  useHeartbeatExamAttemptMutation,
} from "@/store/api/user/examApi";
import { SECTION } from "@/constants/examConstants";
import { delay } from "@/utils/common";
import {
  setClarityTag,
  trackClarityEvent,
  upgradeClaritySession,
} from "@/utils/clarity";

import QuestionsDialog from "./QuestionsDialog";
import NavigationBar from "./NavigationBar";
import WelcomeScreen from "./WelcomeScreen";
import PartInstruction from "./PartInstruction";
import FinalScoreDialog from "./FinalScoreDialog";
import ConfirmSubmitDialog from "./ConfirmSubmitDialog";
import ExamAudioElement from "./ExamAudioElement";
import ExamQuestionView from "./ExamQuestionView";
import useExamFlow from "../../../hooks/useExamFlow";
import {
  saveExamDraft,
  loadExamDraft,
  clearExamDraft,
  sweepStaleExamDrafts,
} from "./examLocalStore";
import { READING_TIME_SECONDS } from "../../../hooks/useExamFlow";

// ── Resume position helpers ────────────────────────────────────────────────
// Given the exam parts tree and the array of already-answered question IDs
// (from the server attempt), work out where the student currently is.
// Returns { partIndex, groupIndex, inReading } or null if not deterministic.
const computeResumeState = (parts, answeredQuestionIds) => {
  if (!parts?.length || !answeredQuestionIds?.length) return null;

  const answered = new Set(answeredQuestionIds.map(String));
  const entries = [];

  for (let pi = 0; pi < parts.length; pi++) {
    const part = parts[pi];
    const groups = part?.questionGroup || [];
    for (let gi = 0; gi < groups.length; gi++) {
      const questions = groups[gi]?.questions || [];
      for (let qi = 0; qi < questions.length; qi++) {
        const qid = questions[qi]?._id;
        if (!qid) continue;
        entries.push({
          partIndex: pi,
          groupIndex: gi,
          section: part?.section || SECTION.LISTENING,
          questionId: String(qid),
        });
      }
    }
  }

  if (!entries.length) return null;

  const latestAnsweredEntry = (() => {
    for (let pi = parts.length - 1; pi >= 0; pi--) {
      const groups = parts[pi]?.questionGroup || [];
      for (let gi = groups.length - 1; gi >= 0; gi--) {
        const questions = groups[gi]?.questions || [];
        for (let qi = questions.length - 1; qi >= 0; qi--) {
          const qid = questions[qi]?._id;
          if (qid && answered.has(String(qid))) {
            return {
              partIndex: pi,
              groupIndex: gi,
              section: parts[pi]?.section || SECTION.LISTENING,
              questionId: String(qid),
            };
          }
        }
      }
    }
    return null;
  })();

  if (!latestAnsweredEntry) return null;

  const listeningEntries = entries.filter((entry) => entry.section === SECTION.LISTENING);
  const readingEntries = entries.filter((entry) => entry.section === SECTION.READING);

  const firstUnansweredReading = readingEntries.find(
    (entry) => !answered.has(entry.questionId)
  );

  let target = null;

  if (latestAnsweredEntry.section === SECTION.LISTENING) {
    // In Listening, continue from the question right after the latest answered one.
    const latestAnsweredListeningIndex = (() => {
      for (let i = listeningEntries.length - 1; i >= 0; i--) {
        if (answered.has(listeningEntries[i].questionId)) return i;
      }
      return -1;
    })();

    for (let i = latestAnsweredListeningIndex + 1; i < listeningEntries.length; i++) {
      if (!answered.has(listeningEntries[i].questionId)) {
        target = listeningEntries[i];
        break;
      }
    }

    if (!target) {
      target = firstUnansweredReading || null;
    }
  } else {
    // In Reading, resume from the earliest unanswered reading question.
    target = firstUnansweredReading || null;
  }

  if (target) {
    return {
      partIndex: target.partIndex,
      groupIndex: target.groupIndex,
      inReading: target.section === SECTION.READING,
    };
  }

  // All questions answered — resume at the very last group.
  const lastPi = parts.length - 1;
  const lastGroups = parts[lastPi]?.questionGroup || [];
  return {
    partIndex: lastPi,
    groupIndex: Math.max(0, lastGroups.length - 1),
    inReading: parts[lastPi]?.section === SECTION.READING,
  };
};

const isValidListeningCursor = (parts, cursor) => {
  if (!parts?.length || !cursor) return false;
  const { partIndex, groupIndex } = cursor;
  if (!Number.isInteger(partIndex) || !Number.isInteger(groupIndex)) {
    return false;
  }
  if (partIndex < 0 || partIndex >= parts.length) return false;
  const part = parts[partIndex];
  if (part?.section !== SECTION.LISTENING) return false;
  const groups = part?.questionGroup || [];
  return groupIndex >= 0 && groupIndex < groups.length;
};

const pickFurthestCursor = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  if (b.partIndex > a.partIndex) return b;
  if (b.partIndex < a.partIndex) return a;
  return b.groupIndex > a.groupIndex ? b : a;
};

const ExamSession = () => {
  const { examId } = useParams();
  const { data } = useGetUserExamByIdQuery(examId);
  const { collapseSidebar } = useSidebar();
  const exam = data?.data;

  const [selectAnswer] = useChooseAnswerMutation();
  const [heartbeatAttempt] = useHeartbeatExamAttemptMutation();
  const [finishExam, { data: finishExamData, isLoading: isFinishingExam }] =
    useFinishExamMutation();

  const currentAttempt = useSelector((state) => state.exam.currentAttempt);

  const [answers, setAnswers] = useState({});
  const [markedQuestions, setMarkedQuestions] = useState({});
  const [openQuestionList, setOpenQuestionList] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openExamResultDialog, setOpenExamResultDialog] = useState(false);
  // Holds the error from the last failed handleSubmit attempt so the
  // FinalScoreDialog can render an explicit "submission failed" state with
  // a Retry button instead of silently rendering nothing.
  const [submitError, setSubmitError] = useState(null);

  // ── Failed-answer retry queue ────────────────────────────────────────
  // Map<questionId, { questionId, selectedOption }> — answers whose POST
  // /answer call failed (after one in-flight retry) and have NOT yet been
  // successfully persisted on the server.
  const failedAnswersRef = useRef(new Map());
  const [failedAnswerCount, setFailedAnswerCount] = useState(0);
  const [isRetryingFailedAnswers, setIsRetryingFailedAnswers] = useState(false);
  // Track in-flight chooseAnswer mutation promises so we can drain them
  // before calling /finish (otherwise the last 1–2 answers can be lost when
  // the user clicks Submit immediately after answering).
  const pendingAnswersRef = useRef([]);
  // Rate-limit the toast so a network outage doesn't spam 50 toasts.
  const lastFailToastAtRef = useRef(0);
  const answersRef = useRef(answers);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // ── Centralised exam flow (audio + navigation + timer) ───────────────
  const flow = useExamFlow({
    exam,
    mode: "full",
    onTimeUp: () => handleSubmit(),
  });
  const {
    audioRef,
    showWelcomeScreen,
    setShowWelcomeScreen,
    showInstructions,
    setShowInstructions,
    setHasUserInteracted,
    currentPart,
    currentGroup,
    questionGroup,
    currentSection,
    currentPartIndex,
    currentGroupIndex,
    isLastQuestion,
    setAudioVolume,
    isPlaying,
    isFinishListening,
    timeLeft,
    isWaiting,
    handleNext,
    handlePrev,
    handleAudioEnded,
    handleAudioPlay,
    navigateToQuestion,
    readingQuestions,
    resumeFrom,
  } = flow;

  // ── Network-quality helpers (shared by /answer and /finish retry) ────
  const isTransient = useCallback((err) => {
    const s = err?.status;
    return (
      s === "FETCH_ERROR" ||
      s === "TIMEOUT_ERROR" ||
      s === 502 ||
      s === 503 ||
      s === 504
    );
  }, []);

  // Send a single /answer call with one retry on transient network errors.
  // On final failure, the answer is queued in `failedAnswersRef` so the
  // banner can offer a manual retry and `handleSubmit` can replay it before
  // calling /finish. On success, any prior failed entry for this question
  // is cleared. The promise is also pushed into `pendingAnswersRef` so
  // `handleSubmit` can drain in-flight saves.
  const sendAnswerWithRetry = useCallback(
    async (questionId, selectedOption) => {
      const attemptId = currentAttempt?._id;
      if (!attemptId) return;

      const markFailed = (err) => {
        failedAnswersRef.current.set(questionId, {
          questionId,
          selectedOption,
        });
        setFailedAnswerCount(failedAnswersRef.current.size);

        trackClarityEvent("exam.answer.saveFailed");
        upgradeClaritySession("answer-save-failed");

        const now = Date.now();
        if (now - lastFailToastAtRef.current > 4000) {
          lastFailToastAtRef.current = now;
          const status = err?.status;
          if (status === 401 || status === 403) {
            toast.error(
              "Phiên đăng nhập hết hạn. Câu trả lời chưa được lưu — vui lòng đăng nhập lại."
            );
          } else if (isTransient(err)) {
            toast.warning(
              "Mạng yếu — một câu trả lời chưa lưu được. Bài thi sẽ thử lại tự động."
            );
          } else {
            toast.error(
              err?.data?.message ||
                "Không lưu được câu trả lời. Vui lòng kiểm tra kết nối và thử lại."
            );
          }
        }
      };

      const markSucceeded = () => {
        if (failedAnswersRef.current.delete(questionId)) {
          setFailedAnswerCount(failedAnswersRef.current.size);
        }
      };

      const fire = () =>
        selectAnswer({
          attemptId,
          body: { questionId, selectedOption },
        });

      const trackPending = (promise) => {
        pendingAnswersRef.current.push(promise);
        const cleanup = () => {
          pendingAnswersRef.current = pendingAnswersRef.current.filter(
            (p) => p !== promise
          );
        };
        Promise.resolve(promise).then(cleanup, cleanup);
        return promise;
      };

      try {
        const promise = trackPending(fire());
        await promise.unwrap();
        markSucceeded();
        return;
      } catch (err) {
        if (!isTransient(err)) {
          markFailed(err);
          return;
        }
        try {
          await delay(1500);
          // Bail if the student already overwrote this answer with a newer
          // selection — no point retrying a stale value.
          if (answersRef.current[questionId] !== selectedOption) return;
          const promise2 = trackPending(fire());
          await promise2.unwrap();
          markSucceeded();
        } catch (err2) {
          markFailed(err2);
        }
      }
    },
    [currentAttempt, selectAnswer, isTransient]
  );

  const retryFailedAnswers = useCallback(async () => {
    if (failedAnswersRef.current.size === 0) return;
    setIsRetryingFailedAnswers(true);
    try {
      const items = Array.from(failedAnswersRef.current.values());
      for (const { questionId, selectedOption } of items) {
        // eslint-disable-next-line no-await-in-loop
        await sendAnswerWithRetry(questionId, selectedOption);
      }
      if (failedAnswersRef.current.size === 0) {
        toast.success("Đã lưu lại tất cả câu trả lời chưa lưu.");
      } else {
        toast.warning(
          `Còn ${failedAnswersRef.current.size} câu chưa lưu được. Vui lòng kiểm tra mạng và thử lại.`
        );
      }
    } finally {
      setIsRetryingFailedAnswers(false);
    }
  }, [sendAnswerWithRetry]);

  // ── Submit flow ──────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!currentAttempt?._id) {
      toast.error("Không tìm thấy phiên thi. Vui lòng thử lại.");
      return;
    }

    // 0) Replay any answers that previously failed to save BEFORE /finish.
    if (failedAnswersRef.current.size > 0) {
      try {
        await retryFailedAnswers();
      } catch (_) {}
      if (failedAnswersRef.current.size > 0) {
        toast.error(
          `Còn ${failedAnswersRef.current.size} câu trả lời chưa lưu được lên hệ thống. Vui lòng kiểm tra mạng và bấm "Lưu lại câu trả lời" trước khi nộp bài.`
        );
        return;
      }
    }

    // 1) Drain in-flight /answer calls so the server has everything before
    //    we ask it to finalize the score.
    if (pendingAnswersRef.current.length > 0) {
      try {
        await Promise.allSettled(
          pendingAnswersRef.current.map((p) => Promise.resolve(p))
        );
      } catch (_) {}
    }

    // 2) Late failures may have populated failedAnswersRef again.
    if (failedAnswersRef.current.size > 0) {
      try {
        await retryFailedAnswers();
      } catch (_) {}
      if (failedAnswersRef.current.size > 0) {
        toast.error(
          `Còn ${failedAnswersRef.current.size} câu trả lời chưa lưu được. Vui lòng thử lại.`
        );
        return;
      }
    }

    // 3) Retry /finish on transient errors (the endpoint is idempotent).
    const backoffs = [1500, 3000];
    const maxAttempts = 3;
    let lastError = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await finishExam(currentAttempt._id).unwrap();
        setSubmitError(null);
        clearExamDraft(currentAttempt._id);
        trackClarityEvent("exam.finish.succeeded");
        setOpenExamResultDialog(true);
        return;
      } catch (error) {
        lastError = error;
        if (attempt < maxAttempts && isTransient(error)) {
          // eslint-disable-next-line no-await-in-loop
          await delay(backoffs[attempt - 1]);
          continue;
        }
        break;
      }
    }

    setSubmitError(lastError);
    setOpenExamResultDialog(true);
    trackClarityEvent("exam.finish.failed");
    upgradeClaritySession("finish-failed");
    const status = lastError?.status;
    if (status === 401 || status === 403) {
      toast.error(
        "Mất kết nối / hết phiên đăng nhập. Vui lòng đăng nhập lại rồi bấm Nộp bài lần nữa — câu trả lời của bạn đã được lưu."
      );
    } else if (isTransient(lastError)) {
      toast.error(
        "Mạng không ổn định, nộp bài thất bại sau nhiều lần thử. Câu trả lời đã được lưu — vui lòng bấm Nộp bài lại."
      );
    } else {
      toast.error(
        lastError?.data?.message || "Nộp bài thất bại. Vui lòng thử lại."
      );
    }
  }, [currentAttempt, finishExam, isTransient, retryFailedAnswers]);

  // ── Answer + mark handlers ──────────────────────────────────────────
  const handleAnswerSelect = (questionId, selectedOption) => {
    setAnswers((prev) => ({ ...prev, [questionId]: selectedOption }));
    void sendAnswerWithRetry(questionId, selectedOption);
  };

  const handleToggleMarkQuestion = useCallback((question) => {
    if (!question?._id) return;
    setMarkedQuestions((prev) => {
      const next = { ...prev };
      if (next[question._id]) delete next[question._id];
      else next[question._id] = true;
      return next;
    });
  }, []);

  // ── Local persistence of `answers` + `failedAnswers` ────────────────
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    sweepStaleExamDrafts();
  }, []);

  // Tag the Clarity session with exam + attempt info so every replay
  // of this page is filterable by examId/attemptId in the dashboard.
  useEffect(() => {
    if (examId) setClarityTag("examId", examId);
    if (currentAttempt?._id) setClarityTag("attemptId", currentAttempt._id);
    if (currentAttempt?._id) trackClarityEvent("exam.session.opened");
  }, [examId, currentAttempt?._id]);

  useEffect(() => {
    const attemptId = currentAttempt?._id;
    if (!attemptId || hasHydratedRef.current) return;

    const draft = loadExamDraft(attemptId);
    if (draft) {
      if (draft.answers && typeof draft.answers === "object") {
        setAnswers((prev) => ({ ...draft.answers, ...prev }));
      }
      if (Array.isArray(draft.failedAnswers) && draft.failedAnswers.length) {
        draft.failedAnswers.forEach(({ questionId, selectedOption }) => {
          if (questionId && selectedOption != null) {
            failedAnswersRef.current.set(questionId, {
              questionId,
              selectedOption,
            });
          }
        });
        setFailedAnswerCount(failedAnswersRef.current.size);
        setTimeout(() => {
          void retryFailedAnswers();
        }, 0);
      }
    }
    hasHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAttempt?._id]);

  useEffect(() => {
    const attemptId = currentAttempt?._id;
    if (!attemptId || !hasHydratedRef.current) return;
    saveExamDraft(attemptId, {
      examId: currentAttempt?.exam || examId,
      answers,
      failedAnswers: Array.from(failedAnswersRef.current.values()),
    });
  }, [
    answers,
    failedAnswerCount,
    currentAttempt?._id,
    currentAttempt?.exam,
    examId,
  ]);

  // ── Persist paused Reading countdown while actively in Reading ───────────
  // This preserves "pause on exit" behavior: if the student leaves and comes
  // back later, we restore the exact latest saved seconds (instead of
  // subtracting wall-clock elapsed time while they were away).
  const lastSavedReadingSecondRef = useRef(null);
  const lastSavedListeningCursorRef = useRef(null);

  useEffect(() => {
    lastSavedListeningCursorRef.current = null;
  }, [currentAttempt?._id]);

  useEffect(() => {
    if (currentSection !== SECTION.READING || showInstructions) return;
    const attemptId = currentAttempt?._id;
    if (!attemptId || !hasHydratedRef.current) return;
    if (!Number.isFinite(timeLeft)) return;

    const rounded = Math.max(0, Math.floor(timeLeft));
    if (lastSavedReadingSecondRef.current === rounded) return;
    lastSavedReadingSecondRef.current = rounded;

    saveExamDraft(attemptId, {
      examId: currentAttempt?.exam || examId,
      answers,
      failedAnswers: Array.from(failedAnswersRef.current.values()),
      readingRemainingSeconds: rounded,
    });
  }, [
    currentSection,
    showInstructions,
    currentAttempt?._id,
    currentAttempt?.exam,
    examId,
    timeLeft,
    answers,
  ]);

  // ── Sync Listening cursor to API on every LC group change ───────────────
  // Fires an immediate best-effort heartbeat so the server knows the furthest
  // group reached (including audio auto-advance). The server only advances the
  // cursor forward, never backward. Local draft is also updated as fallback.
  useEffect(() => {
    if (currentSection !== SECTION.LISTENING || showInstructions) return;
    const attemptId = currentAttempt?._id;
    if (!attemptId || !hasHydratedRef.current) return;

    const cursor = {
      partIndex: currentPartIndex,
      groupIndex: currentGroupIndex,
    };
    if (!isValidListeningCursor(exam?.parts || [], cursor)) return;

    const key = `${cursor.partIndex}:${cursor.groupIndex}`;
    if (lastSavedListeningCursorRef.current === key) return;
    lastSavedListeningCursorRef.current = key;

    // Persist to local draft (silent fallback if API is unavailable).
    saveExamDraft(attemptId, {
      examId: currentAttempt?.exam || examId,
      answers,
      failedAnswers: Array.from(failedAnswersRef.current.values()),
      listeningProgress: cursor,
    });

    // Push to server immediately — best-effort, do not await.
    void heartbeatAttempt({
      attemptId,
      body: { listeningCursor: cursor },
    });
  }, [
    currentSection,
    showInstructions,
    currentAttempt?._id,
    currentAttempt?.exam,
    examId,
    currentPartIndex,
    currentGroupIndex,
    exam?.parts,
    answers,
    heartbeatAttempt,
  ]);

  // ── Server heartbeat (phase 2): sync Reading timer + progress snapshot ───
  // Every few seconds while in Reading, push remaining countdown to API so
  // `/start` resume can restore from server as the source of truth.
  const heartbeatStateRef = useRef({
    attemptId: null,
    shouldSend: false,
    readingRemainingSeconds: READING_TIME_SECONDS,
  });

  useEffect(() => {
    heartbeatStateRef.current = {
      attemptId: currentAttempt?._id || null,
      shouldSend:
        currentSection === SECTION.READING &&
        !showInstructions &&
        !openExamResultDialog &&
        !isFinishingExam,
      readingRemainingSeconds: Math.max(
        0,
        Math.min(READING_TIME_SECONDS, Math.floor(timeLeft || 0))
      ),
    };
  }, [
    currentAttempt?._id,
    currentSection,
    showInstructions,
    openExamResultDialog,
    isFinishingExam,
    timeLeft,
  ]);

  useEffect(() => {
    const pushHeartbeat = async () => {
      const { attemptId, shouldSend, readingRemainingSeconds } =
        heartbeatStateRef.current;
      if (!attemptId || !shouldSend) return;
      try {
        await heartbeatAttempt({
          attemptId,
          body: { readingRemainingSeconds },
        }).unwrap();
      } catch (_) {
        // Best-effort only. Local draft fallback still covers resume.
      }
    };

    const intervalId = setInterval(() => {
      void pushHeartbeat();
    }, 3000);

    const onPageHide = () => {
      void pushHeartbeat();
    };

    window.addEventListener("pagehide", onPageHide);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [heartbeatAttempt]);

  // ── onResumed: called by WelcomeScreen when server says "Resuming…" ──
  const handleResumed = useCallback(
    (attemptData) => {
      if (!exam?.parts) return;

      // Restore previously answered questions from the server attempt.
      const serverResponses = attemptData?.responses || [];
      const answeredIds = serverResponses.map((r) =>
        r.question?._id?.toString?.() || r.question?.toString()
      );

      // Rehydrate answers map so the palette shows correct colour.
      const restoredAnswers = {};
      serverResponses.forEach((r) => {
        const qId = r.question?._id?.toString?.() || r.question?.toString();
        if (qId && r.selectedOption != null) {
          restoredAnswers[qId] = r.selectedOption;
        }
      });
      setAnswers((prev) => ({ ...restoredAnswers, ...prev }));

      const resume = computeResumeState(exam.parts, answeredIds);
      const attemptId = attemptData?._id;
      const draft = attemptId ? loadExamDraft(attemptId) : null;

      // Prefer API listeningCursor (server only advances forward); fall back
      // to local draft if the heartbeat never reached the server.
      const apiListeningCursor =
        isValidListeningCursor(exam.parts, attemptData?.listeningCursor)
          ? {
              partIndex: attemptData.listeningCursor.partIndex,
              groupIndex: attemptData.listeningCursor.groupIndex,
              inReading: false,
            }
          : null;

      const draftListeningCursor = isValidListeningCursor(
        exam.parts,
        draft?.listeningProgress
      )
        ? {
            partIndex: draft.listeningProgress.partIndex,
            groupIndex: draft.listeningProgress.groupIndex,
            inReading: false,
          }
        : null;

      // Merge: API is authoritative; fall back to draft; pick furthest.
      const bestListeningCursor = pickFurthestCursor(
        apiListeningCursor,
        draftListeningCursor
      );

      const listeningResume = resume && !resume.inReading ? resume : null;
      const mergedListeningResume = pickFurthestCursor(
        listeningResume,
        bestListeningCursor
      );

      if (!resume && mergedListeningResume) {
        collapseSidebar?.();
        resumeFrom({
          partIndex: mergedListeningResume.partIndex,
          groupIndex: mergedListeningResume.groupIndex,
          remainingSeconds: READING_TIME_SECONDS,
          startReadingTimer: false,
        });
        return;
      }

      if (!resume) {
        // No deterministic resume position — just start at the beginning.
        collapseSidebar?.();
        setShowWelcomeScreen(false);
        setShowInstructions(true);
        setHasUserInteracted(true);
        return;
      }

      if (mergedListeningResume && !resume.inReading) {
        collapseSidebar?.();
        resumeFrom({
          partIndex: mergedListeningResume.partIndex,
          groupIndex: mergedListeningResume.groupIndex,
          remainingSeconds: READING_TIME_SECONDS,
          startReadingTimer: false,
        });
        return;
      }

      // Student is in Reading — compute remaining timer time.
      // `readingRemainingSeconds` is `null` (not `undefined`) whenever the
      // attempt/draft was saved before Reading was ever reached — that must
      // mean "no seed, use the full default timer", NOT "0 seconds left".
      // Number(null) is 0 (finite!), so isFinite alone can't tell the two
      // apart; guard with a plain typeof check or a stale Listening-only
      // attempt silently zeroes the Reading countdown and auto-submits the
      // instant Reading starts.
      const isFiniteNumber = (value) =>
        typeof value === "number" && Number.isFinite(value);
      const apiRemaining = attemptData?.readingRemainingSeconds;
      const draftRemaining = draft?.readingRemainingSeconds;
      const selectedRemaining = isFiniteNumber(apiRemaining)
        ? apiRemaining
        : (isFiniteNumber(draftRemaining) ? draftRemaining : READING_TIME_SECONDS);
      const remainingSeconds = Math.max(
        0,
        Math.min(READING_TIME_SECONDS, Math.floor(selectedRemaining))
      );

      collapseSidebar?.();
      resumeFrom({
        partIndex: resume.partIndex,
        groupIndex: resume.groupIndex,
        remainingSeconds,
        startReadingTimer: resume.inReading,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exam?.parts, resumeFrom, collapseSidebar]
  );
  // ── Render ──────────────────────────────────────────────────────────
  const handleNavigateToQuestion = useCallback(
    (questionNumber) => {
      const ok = navigateToQuestion(questionNumber, { scope: "reading" });
      if (ok) setOpenQuestionList(false);
    },
    [navigateToQuestion]
  );

  const showNavBar = useMemo(
    () =>
      (isFinishListening && currentSection === SECTION.LISTENING) ||
      (currentSection === SECTION.READING && !showInstructions),
    [isFinishListening, currentSection, showInstructions]
  );

  if (!exam || !currentPart) {
    return (
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <Typography variant="h6" color="text.secondary">
            Loading exam data...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (showWelcomeScreen) {
    return (
      <WelcomeScreen
        exam={exam}
        setShowInstructions={setShowInstructions}
        collapseSidebar={collapseSidebar}
        setHasUserInteracted={setHasUserInteracted}
        setShowWelcomeScreen={setShowWelcomeScreen}
        setAudioVolume={setAudioVolume}
        onResumed={handleResumed}
      />
    );
  }

  return (
    <>
      <ConfirmSubmitDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={() => {
          setOpenConfirmDialog(false);
          handleSubmit();
        }}
      />
      <FinalScoreDialog
        openExamResultDialog={openExamResultDialog}
        handleCloseExamResultDialog={() => {
          setOpenExamResultDialog(false);
          setSubmitError(null);
        }}
        finishExamData={finishExamData}
        submitError={submitError}
        isSubmitting={isFinishingExam}
        onRetry={handleSubmit}
      />
      <ExamAudioElement
        ref={audioRef}
        onPlay={handleAudioPlay}
        onEnded={handleAudioEnded}
        currentSection={currentSection}
        isPlaying={isPlaying}
      />
      {showInstructions ? (
        <PartInstruction currentPart={currentPart} />
      ) : (
        <ExamQuestionView
          examTitle={exam.title}
          currentPart={currentPart}
          currentGroup={currentGroup}
          currentSection={currentSection}
          answers={answers}
          onAnswerSelect={handleAnswerSelect}
          markedQuestions={markedQuestions}
          onToggleMark={handleToggleMarkQuestion}
          showTimer={currentSection === SECTION.READING}
          timeLeft={timeLeft}
          showSubmit={
            isLastQuestion && currentSection === SECTION.READING
          }
          isSubmitting={isFinishingExam}
          onSubmit={() => setOpenConfirmDialog(true)}
          topSlot={
            failedAnswerCount > 0 ? (
              <Alert
                severity="warning"
                sx={{ mb: 2, borderRadius: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={retryFailedAnswers}
                    disabled={isRetryingFailedAnswers}
                  >
                    {isRetryingFailedAnswers ? (
                      <CircularProgress size={16} />
                    ) : (
                      "Lưu lại câu trả lời"
                    )}
                  </Button>
                }
              >
                {`Có ${failedAnswerCount} câu trả lời chưa được lưu lên hệ thống do lỗi mạng. Vui lòng kiểm tra kết nối và bấm "Lưu lại câu trả lời" trước khi nộp bài.`}
              </Alert>
            ) : null
          }
        />
      )}
      {showNavBar && (
        <NavigationBar
          handleNext={handleNext}
          handlePrev={handlePrev}
          currentGroup={currentGroup}
          questionGroup={questionGroup}
          currentPartIndex={currentPartIndex}
          parts={exam.parts}
          currentGroupIndex={currentGroupIndex}
          isWaiting={isWaiting}
          showInstructions={showInstructions}
          handleOpenQuestionList={() => setOpenQuestionList(true)}
        />
      )}
      <QuestionsDialog
        openQuestionList={openQuestionList}
        handleCloseQuestionList={() => setOpenQuestionList(false)}
        getAllReadingQuestions={readingQuestions}
        navigateToQuestion={handleNavigateToQuestion}
        answers={answers}
        markedQuestions={markedQuestions}
      />
    </>
  );
};

export default ExamSession;
